import * as React from 'react';
import {cn} from '@common-web/lib/utils';

interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 'default' | 'elevated' | 'gradient' | 'interactive';
}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({className, variant = 'default', ...props}, ref) => {
    const variants = {
      default: cn(
        'rounded-xl bg-card text-card-foreground',
        'border border-neutral-200/50',
        'shadow-sm hover:shadow-md',
        'transition-all duration-200',
        'relative overflow-hidden',
      ),
      elevated: cn(
        'rounded-xl bg-card text-card-foreground',
        'border-0',
        'shadow-lg hover:shadow-xl',
        'transition-all duration-200',
        'relative overflow-hidden',
        'hover:-translate-y-1',
      ),
      gradient: cn(
        'rounded-xl text-card-foreground',
        'border-0',
        'bg-gradient-to-br from-white to-gray-50',
        'shadow-md hover:shadow-lg',
        'transition-all duration-200',
        'relative overflow-hidden',
        'before:absolute before:inset-0 before:bg-gradient-primary before:opacity-0 before:transition-opacity before:duration-200',
        'hover:before:opacity-5',
      ),
      interactive: cn(
        'rounded-xl bg-card text-card-foreground',
        'border border-neutral-200/50',
        'shadow-sm hover:shadow-lg',
        'transition-all duration-200',
        'relative overflow-hidden',
        'hover:-translate-y-0.5 hover:border-primary-blue-light/30',
        'group',
      ),
    };

    return (
      <div ref={ref} className={cn(variants[variant], className)} {...props}>
        {variant === 'interactive' && (
          <div className='absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary-blue to-indigo opacity-0 group-hover:opacity-100 transition-opacity duration-200' />
        )}
        {props.children}
      </div>
    );
  },
);
Card.displayName = 'Card';

interface CardHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  gradient?: boolean;
}

const CardHeader = React.forwardRef<HTMLDivElement, CardHeaderProps>(
  ({className, gradient = false, ...props}, ref) => (
    <div
      ref={ref}
      className={cn(
        'flex flex-col space-y-1.5 p-6',
        gradient && 'bg-gradient-to-b from-gray-50 to-white',
        className,
      )}
      {...props}
    />
  ),
);
CardHeader.displayName = 'CardHeader';

const CardTitle = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({className, ...props}, ref) => (
    <div
      ref={ref}
      className={cn(
        'text-xl font-heading font-semibold leading-tight tracking-tight text-neutral-900',
        className,
      )}
      {...props}
    />
  ),
);
CardTitle.displayName = 'CardTitle';

const CardDescription = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({className, ...props}, ref) => (
    <div ref={ref} className={cn('text-sm text-muted-foreground', className)} {...props} />
  ),
);
CardDescription.displayName = 'CardDescription';

const CardContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({className, ...props}, ref) => (
    <div ref={ref} className={cn('p-6 pt-0', className)} {...props} />
  ),
);
CardContent.displayName = 'CardContent';

const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({className, ...props}, ref) => (
    <div ref={ref} className={cn('flex items-center p-6 pt-0', className)} {...props} />
  ),
);
CardFooter.displayName = 'CardFooter';

export {Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent};
